/**
 * Gateway Services Singleton
 *
 * Pre-configured service instances for SPA usage.
 * Import individual services directly:
 *
 * @example
 * import { auth, oauth, files } from '@/services/gateway';
 *
 * // Use services
 * const user = await oauth.getCurrentUser();
 * await auth.login({ email, password });
 */

import { createGatewayClient } from './client'
import { createDefaultConfig } from './config'
import { isAuthError } from './errors'
import { createAuthService } from './auth.service'
import { createOAuthService } from './oauth.service'
import { createAccountService } from './account.service'
import { createProfileService } from './profile.service'
import { createApiKeyService } from './api-keys.service'
import { createCreditsService } from './credits.service'
import { createStorageService } from './storage.service'
import { createFilesService } from './files.service'
import { createPersistentFilesService } from './persistent-files.service'
import { createFoldersService } from './folders.service'
import { createActionsService } from './actions.service'
import { createOrganizationsService } from './organizations.service'
import { createProjectsService } from './projects.service'
import { createProjectStorageService } from './project-storage.service'
import { createJobsService } from './jobs.service'
import { createAuthStore } from '../state/auth-store'
import { Workspace } from '@/state/workspace'

// Track if authentication is valid (set by auth store)
let hasValidSession = false

/**
 * Called by auth store to update session validity
 * When session is valid, 401 errors mean authorization failure (not authenticated)
 * When session is not valid, 401 errors mean authentication failure (redirect to login)
 */
export function setSessionValidity(valid: boolean): void {
  hasValidSession = valid
}

// Create shared client instance with 401 redirect handler and project context
const client = createGatewayClient(
  createDefaultConfig({
    onRequest: async (request) => {
      // Add projectId query parameter for project-scoped endpoints
      const projectId = Workspace.projectId.get()
      if (projectId) {
        const url = new URL(request.url)
        // Only add if not already present
        if (!url.searchParams.has('projectId')) {
          url.searchParams.set('projectId', projectId)
          // Read the body as text to preserve it when creating new Request
          // This avoids issues with ReadableStream being consumed during cloning
          const bodyText = await request.text()
          return new Request(url.toString(), {
            method: request.method,
            headers: request.headers,
            body: bodyText || undefined,
            credentials: request.credentials,
          })
        }
      }
      return request
    },
    onError: (error) => {
      // Only redirect to sign-in on 401 if we don't have a valid session
      // If we have a valid session, a 401 means the user doesn't have permission
      // to access this specific resource (authorization), not that their session expired
      // Don't redirect on public auth pages that expect unauthenticated users
      const publicAuthPaths = [
        '/signin',
        '/signup',
        '/callback',
        '/verify-email',
        '/verify-email-sent',
        '/forgot-password',
        '/reset-password',
      ]
      const isPublicAuthPage = publicAuthPaths.some((path) =>
        window.location.pathname.startsWith(path)
      )
      if (isAuthError(error) && !hasValidSession && !isPublicAuthPage) {
        window.location.href = '/signin'
      }
    },
  })
)

// Export the underlying client for advanced usage
export { client }

// Export all service instances
export const auth = createAuthService(client)
export const oauth = createOAuthService(client)
export const account = createAccountService(client)
export const profile = createProfileService(client)
export const apiKeys = createApiKeyService(client)
export const credits = createCreditsService(client)
export const storage = createStorageService(client)
export const files = createFilesService(client)
export const actions = createActionsService(client)
export const organizations = createOrganizationsService(client)
export const projects = createProjectsService(client)

// Project-scoped service factories
// These require a projectId and should be created when needed
export const createProjectPersistentFiles = (projectId: string) =>
  createPersistentFilesService(client, { projectId })
export const createProjectFolders = (projectId: string) =>
  createFoldersService(client, { projectId })
export interface ProjectServices {
  persistentFiles: ReturnType<typeof createProjectPersistentFiles> | null
  folders: ReturnType<typeof createProjectFolders> | null
  jobs: ReturnType<typeof createProjectJobs> | null
  storage: ReturnType<typeof createProjectStorageService> | null
  projectId: string | null
}

export const createProjectJobs = (projectId: string) =>
  createJobsService(client, { projectId })
export const createProjectStorage = (projectId: string) =>
  createProjectStorageService(client, { projectId })

/**
 * Memoized project services - reuses instances when project ID hasn't changed.
 * This utility standardizes the service creation pattern across pages.
 *
 * NOTE: Despite the "use" prefix, this is NOT a React hook. It's a factory function
 * that creates memoized service instances. In Tempo, we don't have hooks - this is
 * just a simple memoization pattern.
 *
 * @example
 * ```typescript
 * const services = useProjectServices(() => currentProjectId.get())
 *
 * // In async handlers:
 * const { persistentFiles, folders, jobs } = services.get()
 * if (persistentFiles) {
 *   const files = await persistentFiles.list()
 * }
 * ```
 */
export function useProjectServices(
  getProjectId: () => string | null | undefined
) {
  let lastProjectId = ''
  let persistentFiles: ReturnType<typeof createProjectPersistentFiles> | null =
    null
  let folders: ReturnType<typeof createProjectFolders> | null = null
  let jobs: ReturnType<typeof createProjectJobs> | null = null
  let storage: ReturnType<typeof createProjectStorageService> | null = null

  return {
    get(): ProjectServices {
      const pid = getProjectId()
      if (!pid) {
        return {
          persistentFiles: null,
          folders: null,
          jobs: null,
          storage: null,
          projectId: null,
        }
      }
      if (pid !== lastProjectId) {
        persistentFiles = createProjectPersistentFiles(pid)
        folders = createProjectFolders(pid)
        jobs = createProjectJobs(pid)
        storage = createProjectStorage(pid)
        lastProjectId = pid
      }
      return { persistentFiles, folders, jobs, storage, projectId: pid }
    },
    /** Invalidate cached services to force recreation on next get() */
    invalidate() {
      lastProjectId = ''
      persistentFiles = null
      folders = null
      jobs = null
      storage = null
    },
  }
}

// Export auth store singleton (reactive state)
export const authStore = createAuthStore(oauth)
